import {
  faCheck,
  faGift,
  faSoccerBall,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { MarketGuidanceLayout } from 'components/onboarding/MarketGuidanceLayout';

import { Step } from './Step';
import captain from './assets/captain.svg';
import rewards from './assets/rewards.jpg';

const StepsWrapper = styled(Vertical)`
  position: relative;
`;
const PlayerPictureWrapper = styled.div`
  padding: var(--unit) var(--unit) 0 var(--unit);
  background: linear-gradient(to bottom, var(--c-nl-500), var(--c-nl-200));
  border-radius: 0 calc(var(--double-unit) - 1px) calc(var(--double-unit) - 1px)
    0;
`;
const PlayerPicture = styled.div`
  height: 100%;
  background: no-repeat center / cover var(--picture-url);
`;
const RelativeHorizontal = styled(Horizontal)`
  position: relative;
`;
const StepsLineWrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: absolute;
  top: var(--double-unit);
  left: var(--double-unit);
  height: calc(100% - 120px + var(--double-and-a-half-unit));
`;
const IconWrapper = styled(Horizontal).attrs({ center: true })`
  aspect-ratio: 1;
  width: var(--triple-unit);
  height: var(--triple-unit);
  border-radius: 50%;
  background-color: var(--c-white);

  &.disabled {
    opacity: 0.5;
  }
`;
const Line = styled.div`
  width: 0;
  flex: 1;
  border: 2px solid var(--c-white);

  &.disabled {
    opacity: 0.5;
    border-style: dashed;
  }
`;

type Props = { onSubmit: () => void; firstCardPictureUrl?: string | null };
export const CaptainTutorial = ({ onSubmit, firstCardPictureUrl }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  return (
    <MarketGuidanceLayout
      cta={
        <Button
          fullWidth={!isTabletAndAbove}
          color="secondary"
          size="medium"
          onClick={onSubmit}
        >
          <FormattedMessage
            id="CaptainTutorial.cta"
            defaultMessage="Select My Captain"
          />
        </Button>
      }
    >
      <Vertical center={isTabletAndAbove}>
        <StarBall />
        <LabelL color="var(--c-nd-700)">
          <FormattedMessage
            id="CaptainTutorial.nextSteps.title"
            defaultMessage="Almost done,{br}here’s what’s next"
            values={{ br: <br /> }}
          />
        </LabelL>
        <RelativeHorizontal>
          <StepsLineWrapper>
            <IconWrapper>
              <FontAwesomeIcon
                icon={faCheck}
                color="var(--c-blue-300)"
                size="sm"
              />
            </IconWrapper>
            <Line />
            <IconWrapper>
              <FontAwesomeIcon
                icon={faSoccerBall}
                color="var(--c-blue-300)"
                size="sm"
              />
            </IconWrapper>
            <Line className="disabled" />
            <IconWrapper className="disabled">
              <FontAwesomeIcon
                icon={faGift}
                color="var(--c-blue-300)"
                size="xs"
              />
            </IconWrapper>
          </StepsLineWrapper>
          <StepsWrapper gap={2.5}>
            <Step
              title={
                <LabelL color="var(--c-nd-600)">
                  <FormattedMessage
                    id="CaptainTutorial.step1.title"
                    defaultMessage="Build your team"
                  />
                </LabelL>
              }
              subtitle={
                <BodyS color="var(--c-nd-400)" style={{ textAlign: 'right' }}>
                  <FormattedMessage
                    id="CaptainTutorial.step1.subtitle"
                    defaultMessage="“Thanks for your trust gaffer.”"
                  />
                </BodyS>
              }
              img={
                firstCardPictureUrl && (
                  <PlayerPictureWrapper>
                    <PlayerPicture
                      style={
                        {
                          '--picture-url': `url(${firstCardPictureUrl})`,
                        } as CSSProperties
                      }
                    />
                  </PlayerPictureWrapper>
                )
              }
            />
            <Step
              highlighted
              title={
                <LabelL color="var(--c-white)">
                  <FormattedMessage
                    id="CaptainTutorial.step2.title"
                    defaultMessage="Select your Captain"
                  />
                </LabelL>
              }
              subtitle={
                <BodyS color="var(--c-nd-700)">
                  <FormattedMessage
                    id="CaptainTutorial.step2.subtitle"
                    defaultMessage="Your players are waiting"
                  />
                </BodyS>
              }
              img={<img src={captain} alt="" />}
            />
            <Step
              title={
                <LabelL color="var(--c-white)">
                  <FormattedMessage
                    id="CaptainTutorial.step3.title"
                    defaultMessage="Win Rewards"
                  />
                </LabelL>
              }
              subtitle={
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="CaptainTutorial.step3.subtitle"
                    defaultMessage="Cash, cards, jerseys, match tickets and more!"
                  />
                </BodyS>
              }
              img={<img src={rewards} alt="" />}
            />
          </StepsWrapper>
        </RelativeHorizontal>
      </Vertical>
    </MarketGuidanceLayout>
  );
};
